import { PropsWithChildren } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  PathValue,
} from "react-hook-form";
import { Autocomplete, Chip, SxProps, TextField, Theme } from "@mui/material";

import { CommonFieldProps } from "./types";

interface Props<T extends FieldValues> extends CommonFieldProps<T> {
  control: Control<T, any>;
  options: [string, string][];
  labelId: string;
  label: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  containerSx?: SxProps<Theme>;
}

const getOptionByValue = (value: string, options: [string, string][]) =>
  options.find(([v]) => v === value);

const FieldAutocompleteMultiSelect: React.FunctionComponent<Props<any>> = <
  TObject extends FieldValues
>({
  control,
  fieldName,
  options,
  labelId,
  label,
  disabled = false,
  sx = {},
  containerSx = {},
}: PropsWithChildren<Props<TObject>>) => (
  <Controller
    control={control}
    name={fieldName}
    defaultValue={[] as PathValue<TObject, Path<TObject>>}
    render={({ field: { onChange, value, ...rest }, fieldState: { error } }) => {
      const selectedOptions = (Array.isArray(value) ? value : []).map(
        (v: string) => getOptionByValue(v, options)
      ).filter(Boolean) as [string, string][];

      return (
        <Autocomplete
          id={labelId}
          multiple
          options={options}
          getOptionLabel={([, label]) => label}
          value={selectedOptions}
          disabled={disabled}
          onChange={(_, newValue) => {
            onChange(newValue.map(([v]) => v));
          }}
          isOptionEqualToValue={([a], [b]) => a === b}
          renderOption={(props, [value, label]) => (
            <li {...props} key={value}>
              {label}
            </li>
          )}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option[0]}
                label={option[1]}
                size="small"
              />
            ))
          }
          sx={containerSx}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant="outlined"
              error={!!error}
              helperText={error?.message}
              inputProps={{
                ...params.inputProps,
                autoComplete: "disabled",
              }}
              sx={sx}
            />
          )}
          {...rest}
        />
      );
    }}
  />
);

export default FieldAutocompleteMultiSelect;
